import clsx from 'clsx';
import PropTypes from 'prop-types';

import useValidDeliveryDaysQuery from 'api/queries/useValidDeliveryDaysNoSubQuery';
import { BOYSENBERRY_S3_IMAGES } from 'constants';
import Card from 'honeydew/Card';
import useGlobalStore from 'hooks/useGlobalStore';
import { CreateAccountButton } from 'shared/CreateAccountButton';
import { formatDateString, getDay, localizeTimeZone } from 'utils/dateUtils';
import { getSelectedDeliveryDay, getSignupLocalStorage } from 'utils/signupUtils';

export default function WhatToExpectFrictionReduction({ renderCreateAccountButton, renderHeadline, shortSummary }) {
    const signupLocalStorage = getSignupLocalStorage();
    const { isMisfitsTheme } = useGlobalStore();

    const { data: validDeliveryDays } = useValidDeliveryDaysQuery({
        extendedQueryKeys: ['signup'],
        funnelType: isMisfitsTheme ? 'misfits' : 'imperfect',
        zip: signupLocalStorage?.zip,
        orderInterval: signupLocalStorage?.orderInterval,
    });

    const selectedDeliveryDay = getSelectedDeliveryDay({ validDeliveryDays });

    const { deliveryDay } = selectedDeliveryDay ?? {};

    const shoppingWindow = selectedDeliveryDay
        ? {
              openDay: getDay(selectedDeliveryDay?.openDate?.date),
              openTime: localizeTimeZone(selectedDeliveryDay?.openDate?.date, true),
              closeDay: getDay(selectedDeliveryDay?.closeDate?.date),
              closeTime: localizeTimeZone(selectedDeliveryDay?.closeDate?.date, true),
          }
        : null;

    const firstDeliveryDate = formatDateString(selectedDeliveryDay?.dateOfFirstOrder?.date, true, false, true);
    const weekly = signupLocalStorage?.weekly ?? true;

    const formatTextForShortSummary = ({ boldText, regularText }) => (
        <span className={clsx({ 'text-body-sm': shortSummary })}>
            <span className={clsx({ 'font-grotesk': shortSummary })}>{regularText} </span>
            {boldText}
        </span>
    );

    return (
        <Card extendedStyles={{ padding: 'px-20 py-25' }} data-optimizely-id="how-itll-work">
            <div className="flex flex-col gap-20">
                {renderHeadline && <h2 className="text-body-lg font-grotesk-bold">How it&apos;ll work</h2>}
                <div className="@container/list">
                    <ul className="flex flex-col gap-20">
                        {shoppingWindow && (
                            <ListItem
                                imgProps={{
                                    alt: 'Shop on mobile',
                                    src: {
                                        if: `${BOYSENBERRY_S3_IMAGES}signup/if-mobile-shop.png`,
                                        mm: `${BOYSENBERRY_S3_IMAGES}signup/mm-calendar.svg`,
                                    },
                                }}
                                text={formatTextForShortSummary({
                                    boldText: `${shoppingWindow.openTime} ${shoppingWindow.openDay} - ${shoppingWindow.closeTime} ${shoppingWindow.closeDay}`,
                                    regularText: 'Shop during your shopping window.',
                                })}
                                subText={
                                    shortSummary
                                        ? undefined
                                        : 'Shop our assortment of over 700 products, including our rescued favorites.'
                                }
                            />
                        )}
                        {firstDeliveryDate && deliveryDay && (
                            <ListItem
                                imgProps={{
                                    alt: 'Delivery to your door',
                                    src: {
                                        if: `${BOYSENBERRY_S3_IMAGES}signup/if-delivery.png`,
                                        mm: `${BOYSENBERRY_S3_IMAGES}signup/mm-door.svg`,
                                    },
                                }}
                                text={formatTextForShortSummary({
                                    boldText: firstDeliveryDate,
                                    regularText: 'Your first delivery will arrive',
                                })}
                                subText={
                                    <span className={clsx({ 'text-body-mobile-xs': shortSummary })}>
                                        Your {weekly ? 'weekly' : 'biweekly'} delivery day is{' '}
                                        <span className="capitalize">{deliveryDay}</span>.
                                    </span>
                                }
                            />
                        )}
                    </ul>
                </div>

                {renderCreateAccountButton && <CreateAccountButton />}
            </div>
        </Card>
    );
}

WhatToExpectFrictionReduction.propTypes = {
    renderCreateAccountButton: PropTypes.bool,
    renderHeadline: PropTypes.bool,
    shortSummary: PropTypes.bool,
};

WhatToExpectFrictionReduction.defaultProps = {
    renderCreateAccountButton: false,
    renderHeadline: true,
    shortSummary: false,
};

function ListItem({ imgProps, subText, text }) {
    const { isMisfitsTheme } = useGlobalStore();

    return (
        <li className="flex items-center gap-20 border-b border-off-white pb-20 last:border-none last:pb-0 @md/list:gap-10 @md/list:border-none">
            <div className="shrink-0">
                <img
                    alt={imgProps?.alt}
                    src={isMisfitsTheme ? imgProps.src.mm : imgProps.src.if}
                    className={clsx('aspect-square w-50', {
                        'rounded-full border border-black': !isMisfitsTheme,
                    })}
                />
            </div>
            <p className="font-grotesk-bold">
                {text}
                {subText && (
                    <>
                        <br />
                        <span className="text-body-sm font-grotesk">{subText}</span>
                    </>
                )}
            </p>
        </li>
    );
}

ListItem.propTypes = {
    imgProps: PropTypes.shape({
        alt: PropTypes.string.isRequired,
        src: PropTypes.shape({
            if: PropTypes.string.isRequired,
            mm: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
    subText: PropTypes.node,
    text: PropTypes.node.isRequired,
};

ListItem.defaultProps = {
    subText: null,
};
